import * as React from "react";
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  Fade,
  Popover,
  styled, Theme,
  Typography,
} from "@mui/material";
import {
  GraphData,
  NodeType,
  QualifiedFeatureFragment,
} from "fe-common/graphql";
import { GraphArea, GraphAreaActions } from "fe-web-components/graphview";
import UndoIcon from "@mui/icons-material/Undo";
import { CoronaEmptyGraphContainer } from "./coronaEmptyGraphContainer";
import { SupportFocusMutation } from "./responsiveGridLayout";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import InfoIcon from "@mui/icons-material/Info";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { featuresToMultiLine } from "fe-common/logic";
import { KahunPrimaryTypography } from "../style/akTypography";
import { GraphDetailSelection } from "../util/graphDetialsSelectionHook";
import { GraphSelectionSummary } from "./graphSelectionSummary";
//region [[ Styles ]]

const GraphContainer = styled("div")({
  width: "100%",
  height: "100%",
});

export const GraphTooltipZIndex = 4;
const GraphInfoToolTipPopover = styled(Popover)(({ theme }) => ({
  margin: theme.spacing(2),
}));

const StyledInfoIcon = styled(InfoIcon)(({ theme }) => ({
  position: "absolute",
  zIndex: GraphTooltipZIndex - 1,
  fill: KahunColorMap.Primary,
  margin: theme.spacing(3),
  fontSize: "xx-large",
  cursor: "pointer",
  opacity: 1,
  right: theme.spacing(2),
  top: 0,
  "&:hover": {
    fill: KahunColorMap.Secondary,
  },
}));

const StyledInfoGotItButton = styled(Button)(({ theme }) => ({}));

const SelectionSummary = styled(GraphSelectionSummary)(({ theme }) => ({
  zIndex: GraphTooltipZIndex - 2,
  position: "absolute",
  right: theme.spacing(1),
  padding: theme.spacing(2),
  paddingRight: "50px", // Leave space for the InfoButton
}));

const ResetViewContainer = styled(Box)(({ theme }) => ({
  position: "absolute",
  zIndex: GraphTooltipZIndex - 2,
  fill: KahunColorMap.Primary,
  margin: theme.spacing(2),
  left: theme.spacing(2),
  opacity: 1,
  top: "70px",
  "&:hover": {
    fill: KahunColorMap.Secondary,
  },
}));

const BoldTypography = styled(KahunPrimaryTypography)(({ theme }: {theme?: Theme, noWrap: any}) => ({
  margin: theme.spacing(0.5),
  color: KahunColorMap.Default,
  fontWeight: 700,
}));

const DiseaseTypography = styled(BoldTypography)({
  color: KahunColorMap.Accent,
});

const FindingsTypography = styled(BoldTypography)({
  color: KahunColorMap.Primary,
});

const NoConnectionsMessage = styled(KahunPrimaryTypography)({
  color: KahunColorMap.Default,
  position: "absolute",
  top: 0,
  bottom: 0,
  width: "100%",
  display: "flex",
  flexWrap: "wrap",
  justifyItems: "center",
  justifyContent: "center",
  alignItems: "center",
  zIndex: GraphTooltipZIndex,
});
//endregion [[ Styles ]]

//region [[ Props ]]
const NO_TOOLBAR = { dontRenderToolbar: true };

export interface DvGraphAreaProps extends SupportFocusMutation {
  source?: QualifiedFeatureFragment;
  graph: GraphData;
  loading: boolean;
  selection: GraphDetailSelection;
  hideSummary?: boolean;
  hideResetButton?: boolean;
  refGraphActions?: MutableRefObject<GraphAreaActions>;
  children?: React.ReactElement | React.ReactElement[];
  defaultSelectedNodeId?: string;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const DVGraphArea = React.memo(
  ({
    source,
    graph,
    loading,
    selection,
    refGraphActions,
    hideResetButton = false,
    defaultSelectedNodeId = null,
    ...props
  }: DvGraphAreaProps) => {
    let graphRef = useRef<GraphAreaActions>();

    //chain this ref to the ref passed in, so that both are always set
    let chainedRef: MutableRefObject<GraphAreaActions> = {
      set current(c: GraphAreaActions) {
        graphRef.current = c;
        if (refGraphActions) refGraphActions.current = c;
      },
      get current() {
        return graphRef.current;
      },
    };

    useEffect(() => {
      if (refGraphActions) refGraphActions.current = graphRef.current;
    }, [graphRef, refGraphActions]);

    let { setSelection } = selection;
    let [focused, setFocused] = useState(false);
    let [showInfo, setShowInfo] = useState(false);

    const infoRef = useRef();

    const edgeSelect = useCallback(
      (select, edges) => {
        select
          ? setSelection({ node: null, edge: edges, detailOpen: false })
          : setSelection({ node: null, edge: null, detailOpen: false });
      },
      [setSelection],
    );

    const nodeSelect = useCallback(
      (select, node) => {
        select
          ? setSelection({ node: node, edge: null, detailOpen: false })
          : setSelection({ node: null, edge: null, detailOpen: false });
      },
      [setSelection],
    );

    const graphWithoutEdges =
      graph?.edges?.length === 0 && graph?.nodes?.length;

    const diseaseName = featuresToMultiLine(
      graph?.nodes?.find((node) => node.type === NodeType.Disease)?.qfs || [],
      ",",
    );
    const findingsNames = graph?.nodes
      ?.filter((node) => node.type === NodeType.Finding)
      .map((n) => featuresToMultiLine(n.qfs, " "))
      .join(", ");

    return (
      <>
        <Box flex={1}>
          <StyledInfoIcon
            ref={infoRef}
            onClick={() => {
              setShowInfo(!showInfo);
            }}
          />
          {!props.isSinglePaneLayout && !props.hideSummary && (
            <SelectionSummary selection={selection} />
          )}
          {!hideResetButton && (
            <ResetViewContainer>
              <Fade in={focused}>
                <Button variant="outlined">
                  <Typography
                    variant="button"
                    color="primary"
                    onClick={() => {
                      graphRef.current?.resetFocus();
                      evt({
                        category: CATEGORY.GRAPH,
                        action: ACTION.RESET,
                      });
                    }}
                  >
                    <UndoIcon />
                    Reset View
                  </Typography>
                </Button>
              </Fade>
            </ResetViewContainer>
          )}
          {props.children}
          <Box height={`100%`} style={{ opacity: showInfo ? 0.3 : 1 }}>
            {/* Override default zIndex to still allow actions on page */}
            {
              <GraphInfoToolTipPopover
                style={{ zIndex: GraphTooltipZIndex }}
                open={showInfo}
                anchorEl={infoRef.current}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <CoronaEmptyGraphContainer>
                  <StyledInfoGotItButton
                    variant="contained"
                    color="primary"
                    onClick={() => setShowInfo(false)}
                  >
                    Got it
                  </StyledInfoGotItButton>
                </CoronaEmptyGraphContainer>
              </GraphInfoToolTipPopover>
            }
            {!!graphWithoutEdges && (
              <NoConnectionsMessage display="inline">
                <Box
                  display="flex"
                  flexWrap="wrap"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Typography display="inline" noWrap>
                    No evidence for a correlation between
                  </Typography>
                  <DiseaseTypography noWrap>{diseaseName}</DiseaseTypography>
                  <Typography display="inline" noWrap>
                    and
                  </Typography>
                  <FindingsTypography noWrap>
                    {findingsNames}
                  </FindingsTypography>
                  <Typography display="inline" noWrap>
                    was found in our sources
                  </Typography>
                </Box>
              </NoConnectionsMessage>
            )}
            {graph && !graphWithoutEdges && (
              <GraphArea
                disease={source}
                graph={graph}
                ref={chainedRef}
                onEdgeSelect={edgeSelect}
                onNodeSelect={nodeSelect}
                onFocusChange={setFocused}
                options={NO_TOOLBAR}
                wrapper={GraphContainer}
                showIsA={true}
                defaultSelectedNodeId={defaultSelectedNodeId}
              />
            )}
          </Box>
        </Box>
        {loading && (
          <Box
            width="100%"
            top={0}
            bottom={0}
            zIndex={5}
            position={"absolute"}
            display="flex"
            flexDirection="row"
            justifyItems="center"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress />
          </Box>
        )}
      </>
    );
  },
);
